import { state } from './state.js';
import { generateCard } from './ui.js';
import { placeFetchData, placeFetchDescription } from './api.js';
import { escapeHtml as escapeAttr } from './helpers.js';

/**
 * Load results for Online Businesses modal
 */
export function loadOnlineBuisnessesModalResults() {
	let modal = document.getElementById('search-modal-ob');
	let modalBody = modal.querySelector('#results-modal-body-ob');
	let modalTitle = modal.querySelector('#titleTarget');

	function setupSearchImageObserver() {
		let observerOptions = {
			rootMargin: '0px',
			threshold: 1.0
		};
		
		const searchObserver = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting && Math.floor(entry.intersectionRatio) === 1) {
					let image = entry.target.querySelector('.location-pic');
					let imageURL = image.dataset.background;
					let placeID = entry.target.dataset.markerId;
					image.style.background = `url(${imageURL})`;
					image.style.filter = null;
					
					let target = state.onlineOnlyBusinesses.businesses.find(business => {
						return business.id === parseInt(placeID);
					});
					
					let description = entry.target.querySelector('.seach-card-descripton');
					if (target) {
						description.innerHTML = target.description;
					}
					description.classList.remove('blur');
					searchObserver.unobserve(entry.target);
				}
			});
		}, observerOptions);
		
		return searchObserver;
	}

	let searchObserver = setupSearchImageObserver();

	if (modalBody?.innerHTML) {
		modalBody.innerHTML = '';
	}
	if (modalTitle?.innerHTML) {
		modalTitle.innerHTML = '';
	}
	
	state.onlineOnlyBusinesses.businesses.forEach(function (business) {
		let card = generateCard(business, 'abc123.png', true, true);
		modalBody.appendChild(card);
		searchObserver.observe(card);
	});
}

/**
 * Compose Online Only Modal
 */
export function composeOnlineOnlyModal(id = null) {
	if (id === null) {
		return;
	}
	
	let index = state.onlineOnlyBusinesses.businesses.findIndex(obj => {
		return obj.id === parseInt(id);
	});
	
	let business = index !== -1 ? state.onlineOnlyBusinesses.businesses[index] : false;
	if (!business) {
		return;
	} else {
		state.lastViewedId = business.id;
		let modal = document.getElementById('onlineLocation');
		let modalTitleElement = modal.querySelector('.location-modal-title');
		let modalPhoneElement = modal.querySelector('.phone');
		let modalAddressElement = modal.querySelector('.location-modal-address');
		let modalHoursElement = modal.querySelector('.location-modal-hours');
		let modalDescriptionElement = modal.querySelector('.location-modal-description');
		let modalPicElement = modal.querySelector('.location-pic');
		let modalThumbnailsElement = modal.querySelector('.thumbnailNav');
		let modalInterestsButtonElement = modal.querySelector('.location-modal-interests');
		let modalOwnerButtonElement = modal.querySelector('.location-modal-owner');
		let modalChamberProfileBadgeElement = modal.querySelector('.location-modal-chamber-profile-badge');
		let modalWebsiteLinkElement = modal.querySelector('.location-modal-website-link');

		modalTitleElement.innerHTML = escapeAttr(business.title);
		modalPhoneElement.innerHTML = escapeAttr(business.phone ?? '');
		
		modalInterestsButtonElement.dataset.postId = business.id;
		modalInterestsButtonElement.dataset.title = business.title;

		// Meet the Owner logic
		if (modalOwnerButtonElement) {
			const ownerLink = business.owner_permalink || '#null';

			modalOwnerButtonElement.setAttribute('href', ownerLink);
			modalOwnerButtonElement.setAttribute('target', '_blank');
			modalOwnerButtonElement.style.display = ownerLink !== '#null' ? 'inline-block' : 'none';
		}

		// Badge logic
		if (modalChamberProfileBadgeElement) {
			const chamberLink = business.chamber_profile_url || business.social_media_links?.chamber || '#null';
			const showBadge = business.is_chamber_member && chamberLink !== '#null';

			modalChamberProfileBadgeElement.setAttribute('href', chamberLink);
			modalChamberProfileBadgeElement.style.setProperty('display', showBadge ? 'inline-flex' : 'none', 'important');
		}

		if (modalWebsiteLinkElement) {
			modalWebsiteLinkElement.setAttribute('href', business.website || business.social_media_links?.website || '#null');
		}

		// Social Media Links
		const targetSocial = modal.querySelector('.social');
		if (targetSocial) {
			const facebookURL = business.social_media_links?.facebook;
			const twitterURL = business.social_media_links?.twitter;
			const instagramURL = business.social_media_links?.instagram;
			const linkedinURL = business.social_media_links?.linkedin;

			Array.from(targetSocial.children).forEach(function (item) {
				let socialElement = item.dataset.social;
				let link = item.querySelector('a');
				switch (socialElement) {
					case 'facebook':
						if (!facebookURL) {
							item.style.display = 'none';
						} else {
							link.href = facebookURL;
							item.style.display = 'inline-block';
						}
						break;
					case 'instagram':
						if (!instagramURL) {
							item.style.display = 'none';
						} else {
							link.href = instagramURL;
							item.style.display = 'inline-block';
						}
						break;
					case 'twitter':
						if (!twitterURL) {
							item.style.display = 'none';
						} else {
							link.href = twitterURL;
							item.style.display = 'inline-block';
						}
						break;
					case 'linkedin':
						if (!linkedinURL) {
							item.style.display = 'none';
						} else {
							link.href = linkedinURL;
							item.style.display = 'inline-block';
						}
						break;
				}
			});
		}
		
		if (business.address) {
			modalAddressElement.innerHTML = escapeAttr(business.address);
		} else {
			modalAddressElement.innerHTML = '';
		}
		
		modalHoursElement.innerHTML = "";
		modalHoursElement.style.display = 'none';
		
		// Skeleton Loader for Description
		modalDescriptionElement.innerHTML = `
			<div class="skeleton-text" style="width: 100%;"></div>
			<div class="skeleton-text" style="width: 90%;"></div>
			<div class="skeleton-text" style="width: 80%;"></div>
		`;
		modalDescriptionElement.style.filter = null;
		
		// Update Description once real content is available
		setTimeout(() => {
			modalDescriptionElement.innerHTML = business.description || "No description available.";
		}, 300);
		
		modalThumbnailsElement.innerHTML = '';

		$(modal).foundation('open');
	}
}

/**
 * Output Modal Data for a Marker
 */
export function outputModalData(marker = null) {
	if (marker === null) {
		return;
	}
	let placeId = marker.properties.place_id;
	
	if ((typeof marker.places_results === 'undefined' || typeof marker.places_results.place_data === 'undefined') && placeId !== '') {
		placeFetchData(placeId, 'atmospheric').then((response) => {
			if (typeof marker.places_results === 'undefined') {
				marker.places_results = {};
			}
			marker.places_results.place_data = response?.result;
			marker.places_results.photos = response?.result?.photos;
			outputModalDataStageTwo(marker);
		}).catch(e => console.log(e));
	} else {
		if (placeId === '') {
			marker.places_results = marker.places_results || {};
			marker.places_results.place_data = { formatted_address: '' };
		}
		outputModalDataStageTwo(marker);
	}
}

/**
 * Stage Two of Output Modal Data
 */
export function outputModalDataStageTwo(marker) {
	if (marker === null) {
		return;
	}
	
	let hasNoPlacesPhoto = false;
	let useOverridePhoto = false;
	let hasNoPlacesThumbs = false;
	let useOverrideThumbnails = false;
	let dataOverrides = marker.overrides || [];
	
	state.lastViewedId = marker.properties.post_id;

	if (typeof marker.places_results?.photos === 'undefined') {
		hasNoPlacesPhoto = true;
		hasNoPlacesThumbs = true;
		if (dataOverrides.images && dataOverrides.images.length > 0) {
			useOverridePhoto = true;
			if (dataOverrides.images.length > 1) {
				useOverrideThumbnails = true;
			}
		}
	} else if (marker.places_results.photos.length === 1) {
		hasNoPlacesThumbs = true;
		if (dataOverrides.images && dataOverrides.images.length > 1) {
			useOverrideThumbnails = true;
		}
	}

	let modal = document.getElementById('location');
	let modalTitleElement = modal.querySelector('.location-modal-title');
	let modalPhoneElement = modal.querySelector('.phone');
	let modalAddressElement = modal.querySelector('.location-modal-address');
	let modalHoursElement = modal.querySelector('.location-modal-hours');
	let modalDescriptionElement = modal.querySelector('.location-modal-description');
	let modalPicElement = modal.querySelector('.location-pic');
	let modalThumbnailsElement = modal.querySelector('.thumbnailNav');
	let modalInterestsButtonElement = modal.querySelector('.location-modal-interests');
	let modalOwnerButtonElement = modal.querySelector('.location-modal-owner');
	let modalChamberProfileBadgeElement = modal.querySelector('.location-modal-chamber-profile-badge');
	let modalDirectionsLinkElement = modal.querySelector('.location-modal-directions-link');
	let modalWebsiteLinkElement = modal.querySelector('.location-modal-website-link');

	modalTitleElement.innerHTML = escapeAttr(marker.title);
	modalPhoneElement.innerHTML = escapeAttr(dataOverrides['phone_number'] ?? marker?.places_results?.place_data?.formatted_phone_number ?? '');

	let address = dataOverrides['address'] ?? marker?.places_results?.place_data?.formatted_address;
	if (modalAddressElement) modalAddressElement.innerHTML = escapeAttr(address || '');
	
	if (modalDescriptionElement) {
		modalDescriptionElement.innerHTML = `
			<div class="skeleton-text" style="width: 100%;"></div>
			<div class="skeleton-text" style="width: 90%;"></div>
			<div class="skeleton-text" style="width: 80%;"></div>
		`;
		modalDescriptionElement.style.filter = null;
	}
	
	if (modalThumbnailsElement) modalThumbnailsElement.innerHTML = '';

	if (modalPicElement) {
		// Reset and add blur before loading new image
		modalPicElement.classList.add('blur');
		modalPicElement.style.filter = 'blur(8px) grayscale(0.5)'; // Immediate override
		
		const applyImage = (url) => {
			const img = new Image();
			img.onload = () => {
				modalPicElement.style.background = `url(${url}) center center`;
				modalPicElement.classList.remove('blur');
				modalPicElement.style.filter = '';
			};
			img.src = url;
		};

		if (hasNoPlacesPhoto) {
			if (useOverridePhoto) {
				applyImage(dataOverrides.images[0]);
			} else {
				applyImage('/wp-content/themes/bcc_theme/images/placeholder.png');
			}
		} else {
			const photoUrl = `/wp-content/plugins/geojson-generator/get-place-images.php?photo_ref=${marker.places_results.photos[0].photo_reference}`;
			applyImage(photoUrl);
		}
	}

	// Set Data Id On Button
	if (modalInterestsButtonElement) {
		modalInterestsButtonElement.dataset.postId = marker.properties.post_id;
		modalInterestsButtonElement.dataset.title = marker.title;
	}

	// Meet the Owner Button logic
	const isChamberMember = marker.is_chamber_member || marker.properties?.is_chamber_member || false;
	if (modalOwnerButtonElement) {
		const ownerLink = marker.properties?.owner_permalink || '#null';

		modalOwnerButtonElement.setAttribute('href', ownerLink);
		modalOwnerButtonElement.setAttribute('target', '_blank');
		modalOwnerButtonElement.style.display = ownerLink !== '#null' ? 'inline-block' : 'none';
	}

	// Badge logic
	if (modalChamberProfileBadgeElement) {
		const chamberLink = marker.properties?.chamber_profile_url || marker.properties?.social_media?.chamber || '#null';
		const showBadge = isChamberMember && chamberLink !== '#null';

		modalChamberProfileBadgeElement.setAttribute('href', chamberLink);
		modalChamberProfileBadgeElement.style.setProperty('display', showBadge ? 'inline-flex' : 'none', 'important');
	}

	if (modalDirectionsLinkElement) {
		let directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || '')}&destination_place_id=${marker.properties.place_id}`;
		
		// Mobile deep linking
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
		if (isIOS) {
			// Apple Maps deep link is often preferred on iOS as it doesn't require Google Maps app
			directions = `http://maps.apple.com/?daddr=${encodeURIComponent(address || '')}`;
		}
		
		modalDirectionsLinkElement.setAttribute('href', directions);
	}

	if (modalWebsiteLinkElement) {
		modalWebsiteLinkElement.setAttribute('href', dataOverrides['website'] ?? marker?.places_results?.place_data?.website ?? '#null');
	}

	// Social Media Links
	const targetSocial = modal.querySelector('.social');
	if (targetSocial) {
		const facebookURL = marker?.properties?.social_media?.facebook;
		const twitterURL = marker?.properties?.social_media?.twitter;
		const instagramURL = marker?.properties?.social_media?.instagram;
		const linkedinURL = marker?.properties?.social_media?.linkedin;

		Array.from(targetSocial.children).forEach(function (item) {
			let socialElement = item.dataset.social;
			let link = item.querySelector('a');
			switch (socialElement) {
				case 'facebook':
					if (!facebookURL) {
						item.style.display = 'none';
					} else {
						link.href = facebookURL;
						item.style.display = 'inline-block';
					}
					break;
				case 'instagram':
					if (!instagramURL) {
						item.style.display = 'none';
					} else {
						link.href = instagramURL;
						item.style.display = 'inline-block';
					}
					break;
				case 'twitter':
					if (!twitterURL) {
						item.style.display = 'none';
					} else {
						link.href = twitterURL;
						item.style.display = 'inline-block';
					}
					break;
				case 'linkedin':
					if (!linkedinURL) {
						item.style.display = 'none';
					} else {
						link.href = linkedinURL;
						item.style.display = 'inline-block';
					}
					break;
			}
		});
	}
    
    // Fetch description if available
    if (modalDescriptionElement) {
		placeFetchDescription(marker.properties.post_id).then(response => {
			if (response && response[0]?.acf?.location_description) {
				modalDescriptionElement.innerHTML = response[0].acf.location_description;
				modalDescriptionElement.style.filter = null;
			} else {
				modalDescriptionElement.innerHTML = "No description available.";
				modalDescriptionElement.style.filter = null;
			}
		}).catch(() => {
			modalDescriptionElement.innerHTML = "No description available.";
			modalDescriptionElement.style.filter = null;
		});
	}

	// Load Hours
	let hours = marker?.places_results?.place_data?.opening_hours?.weekday_text;
	if (modalHoursElement) {
		modalHoursElement.innerHTML = '';
		modalHoursElement.style.display = 'none';
		if (typeof dataOverrides['hours'] !== 'undefined') {
			modalHoursElement.style.display = 'block';
			modalHoursElement.innerHTML = dataOverrides['hours'];
		} else if (typeof hours !== 'undefined') {
			modalHoursElement.style.display = 'block';
			let ul = document.createElement('ul');
			ul.classList.add('hours-list');
			for (const hoursElement of hours) {
				let tempLi = document.createElement('li');
				tempLi.innerHTML = hoursElement;
				tempLi.classList.add('hours-list-item');
				ul.appendChild(tempLi);
			}
			modalHoursElement.appendChild(ul);
		}
	}

	$(modal).foundation('open');
}
